import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { createHoliday, getHolidayOptions } from "services/hrms.api";
import Loading from "components/common/Loading";

export default function HolidayCreatePage() {
  const nav = useNavigate();

  // dropdown options
  const [options, setOptions] = useState({ types: [], regions: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // inline form state (no separate component)
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    name: "",
    type: "",
    region: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getHolidayOptions(); // { types, regions }

        // console.log("Fetched Holidays", data);

        const types = data?.types?.length
          ? data.types
          : ["National", "Festival", "Company"];
        const regions = (data?.regions?.length
          ? data.regions
          : ["All"]
        ).includes("All")
          ? data.regions
          : ["All", ...(data?.regions || [])];

        setOptions({ types, regions });
        setForm((p) => ({
          ...p,
          type: p.type || types[0] || "Festival",
          region: p.region || regions[0] || "All",
        }));
      } catch {
        const types = ["National", "Festival", "Company"];
        const regions = ["All"];
        setOptions({ types, regions });
        setForm((p) => ({
          ...p,
          type: p.type || types[0],
          region: p.region || regions[0],
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    // simple guards
    if (!form.date || !form.name) {
      setErr("Please fill date and name.");
      return;
    }

    try {
      await createHoliday(form);
      nav("/admin/hrms/holidays");
    } catch (e2) {
      const msg = e2?.response?.data?.message || "Failed to create holiday.";
      setErr(msg);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h2 className="text-2xl font-semibold dark:text-white">Add Holiday</h2>
      </div>

      {/* Card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        {loading ? (
          <Loading variant="dots" fullscreen />
        ) : (
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            {err && (
              <div className="bg-rose-50 text-rose-700 rounded-lg p-3 text-sm md:col-span-2">
                {err}
              </div>
            )}

            {/* Date */}
            <Field label="Date">
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={setField}
                required
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white dark:bg-navy-800"
              />
            </Field>

            {/* Name */}
            <Field label="Name">
              <input
                name="name"
                value={form.name}
                onChange={setField}
                placeholder="e.g., Independence Day"
                required
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white dark:bg-navy-800"
              />
            </Field>

            {/* Type */}
            <Field label="Type">
              <select
                name="type"
                value={form.type}
                onChange={setField}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white dark:bg-navy-800"
              >
                {(options.types?.length
                  ? options.types
                  : ["Festival", "National", "Company"]
                ).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>

            {/* Region */}
            <Field label="Region">
              <select
                name="region"
                value={form.region}
                onChange={setField}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white dark:bg-navy-800"
              >
                {(options.regions?.length ? options.regions : ["All"]).map(
                  (r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  )
                )}
              </select>
            </Field>

            {/* Actions */}
            <div className="flex justify-end gap-3 md:col-span-2">
              <button
                type="button"
                onClick={() => nav(-1)}
                className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:hover:bg-navy-700 dark:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                Save
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------- small field wrapper ------- */
function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-white/80">
        {label}
      </label>
      {children}
    </div>
  );
}
